import { useState } from 'react';
import { DropdownMenu } from 'radix-ui';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, CircleHelp, Compass, KeyRound, LogOut, Search } from 'lucide-react';
import { useHelp } from '@/app/help/HelpProvider';
import { useAuth } from '@/auth/AuthProvider';
import { fxQuery } from '@/features/fx/api';
import { Kbd } from '@/design/components/Display';
import { LogoDisc } from '@/design/components/Logo';
import { formatDecimal } from '@/lib/format/decimal';
import { formatRelative } from '@/lib/format/dates';
import { cx } from '@/lib/cx';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { Link } from 'react-router';

export function Topbar({ onSearch }: { onSearch: () => void }) {
  const { me, logout, can } = useAuth();
  const [password, setPassword] = useState(false);
  const fx = useQuery({ ...fxQuery(), enabled: can('fx.read') });
  const help = useHelp();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-surface-1/85 px-4 backdrop-blur-md sm:px-6">
      <Link to="/inicio" className="lg:hidden" aria-label="Inicio">
        <LogoDisc size={34} />
      </Link>
      <button
        type="button"
        onClick={onSearch}
        data-tour="search"
        className="group flex h-10 min-w-0 max-w-md flex-1 items-center gap-2.5 rounded-xl border border-line bg-surface-0 px-3.5 text-left text-[13.5px] text-ink-400 shadow-e1 transition hover:border-gold-300"
      >
        <Search className="size-4 transition group-hover:text-gold-600" />
        <span className="flex-1 truncate">Buscar un cliente, presupuesto o trabajo…</span>
        <span className="hidden items-center gap-1 sm:flex">
          <Kbd>Ctrl</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <div className="flex-1" />
      {fx.data && (
        <Link
          to="/cotizacion"
          className="hidden items-center gap-2 rounded-xl border border-line bg-surface-0 px-3 py-1.5 text-[12.5px] shadow-e1 transition hover:border-gold-300 md:flex"
          title="Precio del dólar oficial que usamos hoy para pasar de dólares a pesos"
        >
          <span className={cx('size-1.5 rounded-full', fx.data.usableForIssue ? 'bg-jade-600' : 'bg-terra-600')} />
          <span className="text-ink-500">Dólar</span>
          <span className="tabular font-semibold">$ {formatDecimal(fx.data.sale, { min: 2, max: 2 })}</span>
          <span className="text-ink-400">· {formatRelative(fx.data.publishedAt)}</span>
        </Link>
      )}
      <button
        type="button"
        onClick={help.openHelp}
        data-tour="help"
        className="flex h-10 shrink-0 items-center gap-2 rounded-xl border border-gold-200 bg-gold-50/70 px-3 text-[13.5px] font-medium text-gold-700 shadow-e1 transition hover:border-gold-300 hover:bg-gold-50"
      >
        <CircleHelp className="size-[18px]" />
        <span className="hidden sm:inline">Ayuda</span>
        <span className="sr-only sm:hidden">Ayuda</span>
      </button>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="flex shrink-0 items-center gap-2 rounded-xl px-2 py-1.5 text-[13px] text-ink-700 transition hover:bg-surface-2">
          <span className="flex size-8 items-center justify-center rounded-full border border-gold-200 bg-gold-50 text-[12px] font-semibold text-gold-700">
            {me?.user.name?.[0]?.toUpperCase() ?? '·'}
          </span>
          <span className="hidden max-w-32 truncate sm:block">{me?.user.name}</span>
          <ChevronDown className="size-3.5 text-ink-400" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="z-[60] min-w-56 rounded-xl border border-line bg-surface-0 p-1 shadow-e3 data-[state=open]:animate-rise"
          >
            <div className="px-3 py-2">
              <div className="text-[13px] font-semibold">{me?.user.name}</div>
              <div className="text-[12px] text-ink-500">{me?.user.email}</div>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-line" />
            <DropdownMenu.Item
              onSelect={() => setPassword(true)}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] outline-none data-highlighted:bg-surface-2"
            >
              <KeyRound className="size-4 text-ink-400" /> Cambiar contraseña
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={help.startTour}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] outline-none data-highlighted:bg-surface-2"
            >
              <Compass className="size-4 text-ink-400" /> Ver el recorrido de bienvenida
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => void logout()}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] text-danger-600 outline-none data-highlighted:bg-danger-50"
            >
              <LogOut className="size-4" /> Salir
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <ChangePasswordDialog open={password} onOpenChange={setPassword} />
    </header>
  );
}
